import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/lib/AuthContext";
import { MessageSquare, Plus, Loader2, ShieldAlert, Trash2, Flag, Shield, Ban, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ForumAvatar from "@/components/ForumAvatar";
import ForumConsentModal from "@/components/ForumConsentModal";
import { useToast } from "@/hooks/use-toast";

export default function Forum() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data: meData } = await API.get("/forum/me");
      setMe(meData);
      if (meData.has_access && meData.consented && !meData.banned) {
        const { data } = await API.get("/forum/threads");
        setThreads(data || []);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Forum unavailable", description: typeof detail === "string" ? detail : "", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Clear forum_reply notifications when the forum is opened
  useEffect(() => {
    API.post("/notifications/mark-read-by-type", { type: "forum_reply" })
      .then(() => window.dispatchEvent(new CustomEvent("notifications:refresh")))
      .catch(() => {});
  }, []);

  const createThread = async () => {
    if (!newTitle.trim() || !newBody.trim() || posting) return;
    setPosting(true);
    try {
      await API.post("/forum/threads", { title: newTitle.trim(), body: newBody.trim() });
      setNewOpen(false); setNewTitle(""); setNewBody("");
      await load();
      toast({ title: "Posted!" });
    } catch (err) {
      toast({ title: "Couldn't post", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally {
      setPosting(false);
    }
  };

  const deleteThread = async () => {
    if (!deleteTarget) return;
    try {
      await API.delete(`/forum/threads/${deleteTarget.id}`);
      setDeleteTarget(null);
      toast({ title: "Thread removed" });
      load();
    } catch {
      toast({ title: "Couldn't delete", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="forum-loading">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!me?.has_access) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="forum-no-access">
        <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h2 className="font-display text-xl font-semibold">Complete onboarding first</h2>
        <p className="text-sm text-muted-foreground mt-2">Once your Elevated Psychiatry record is linked, you'll be able to join the ADHD Club Forum.</p>
        <Button className="mt-4 rounded-full" onClick={() => navigate("/onboarding")}>Finish setup</Button>
      </div>
    );
  }

  if (me.banned) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="forum-banned">
        <Ban className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="font-display text-xl font-semibold">You've been removed from the forum</h2>
        <p className="text-sm text-muted-foreground mt-2">Please reach out to your care team via Messages if you have questions.</p>
      </div>
    );
  }

  if (!me.consented) {
    return <ForumConsentModal onDone={load} reconsent={me.reconsent_needed} existingDisplayName={me.display_name} existingAvatarMode={me.avatar_mode} />;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5 animate-fade-in" data-testid="forum-page">
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate("/")}
          className="p-2 rounded-lg hover:bg-accent transition-colors shrink-0"
          aria-label="Back to home"
          data-testid="forum-back-home"
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">ADHD Club Forum</h1>
          <p className="text-muted-foreground text-sm">Welcome, {me.display_name}. Share ideas, swap strategies, cheer each other on.</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex gap-2">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Reminder: this is a public community space. Anything you share is at your own risk and visible to all registered members.
          For medical questions, use <Link className="underline font-medium" to="/messages">Messages</Link>.
        </span>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">
          {threads.length} {threads.length === 1 ? "discussion" : "discussions"}
        </h3>
        <Button size="sm" className="rounded-full gap-1" onClick={() => setNewOpen(true)} data-testid="forum-new-thread-button">
          <Plus className="w-4 h-4" /> New discussion
        </Button>
      </div>

      {newOpen && (
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="forum-new-thread-form">
          <div>
            <Label className="text-xs font-medium">Title</Label>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={120}
              placeholder="What's on your mind?"
              className="mt-1"
              data-testid="forum-new-title-input"
            />
            <p className="text-[10px] text-muted-foreground text-right mt-0.5">{newTitle.length}/120</p>
          </div>
          <div>
            <Label className="text-xs font-medium">Message</Label>
            <Textarea
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              maxLength={5000}
              rows={5}
              placeholder="Share your thoughts… (share at your own risk)"
              className="mt-1"
              data-testid="forum-new-body-input"
            />
            <p className="text-[10px] text-muted-foreground text-right mt-0.5">{newBody.length}/5000</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setNewOpen(false)} disabled={posting}>
              Cancel
            </Button>
            <Button className="flex-1 rounded-full" onClick={createThread} disabled={posting || !newTitle.trim() || !newBody.trim()} data-testid="forum-new-submit-button">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post"}
            </Button>
          </div>
        </div>
      )}

      {threads.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-2xl" data-testid="forum-empty-state">
          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No discussions yet. Be the first to start one!</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="forum-thread-list">
          {threads.map((t) => (
            <li key={t.id}>
              <Link
                to={`/forum/${t.id}`}
                className="block bg-card border border-border rounded-2xl p-4 hover:border-primary/40 transition-colors"
                data-testid={`forum-thread-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <ForumAvatar post={t} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{t.author_display_name}</span>
                      {t.author_is_provider && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                          <Shield className="w-2.5 h-2.5" /> Care Team
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        · {formatDistanceToNow(new Date(t.created_date), { addSuffix: true })}
                      </span>
                    </div>
                    <h4 className={`font-semibold mt-1 ${t.deleted ? "text-muted-foreground italic" : ""}`}>{t.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5 whitespace-pre-wrap">{t.body}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.reply_count || 0}</span>
                      {(t.flags?.length || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`forum-thread-flag-count-${t.id}`}>
                          <Flag className="w-3 h-3" /> {t.flags.length} flag{t.flags.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && !t.deleted && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(t); }}
                      className="p-2 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                      data-testid={`forum-thread-delete-${t.id}`}
                      aria-label="Delete thread"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="forum-delete-thread-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently remove this thread?</AlertDialogTitle>
            <AlertDialogDescription>
              This is permanent. The thread and all its replies will be erased from the forum — members will see no trace of it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteThread} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
